import React from 'react';
import {useSelector} from 'react-redux';
import {cn} from '@/utils';
import {matrixSelector} from '../../slices/matrixSlice';
import {appSelector} from '../../slices/appSlice';

// Zero-width unicode markers used as temporary placeholders in the Drupal.t() translation.
// After translation, we split the string on these markers to wrap the dynamic values
// (quantity/label and price) in bold <span> elements while preserving translator word order.
const COUNT_MARKER = '\u200B\u200C';
const PRICE_MARKER = '\u200C\u200B';
const MARKER_REGEX = new RegExp(`(${COUNT_MARKER}|${PRICE_MARKER})`);

const PriceSummary = () => {
  const {prices, isFetching} = useSelector(matrixSelector);
  const {priceDisplay} = useSelector(appSelector);

  if (!prices?.selection_price) {
    return null;
  }

  const priceSequence = priceDisplay.split('_');
  const primaryPrice = prices.selection_price[priceSequence[0]];

  if (!primaryPrice) {
    return null;
  }

  // Data
  const effectivePrice = primaryPrice.formatted_special_price_total || primaryPrice.formatted_price_total;
  const quantity = prices.selection_quantity.toLocaleString();
  const label = prices.selection_quantity === 1
    ? prices.labels.singular_price_label
    : prices.labels.plural_price_label;
  const countLabel = `${quantity} ${label}`;
  const vatText = priceSequence[0] === 'net'
    ? Drupal.t('excl. VAT', {}, {context: 'Configurator price summary'})
    : Drupal.t('incl. VAT', {}, {context: 'Configurator price summary'});

  // Translation: single Drupal.t() call with markers for bold splitting
  const markedText = Drupal.t('@countLabel for @price @vat', {
    '@countLabel': COUNT_MARKER,
    '@price': PRICE_MARKER,
    '@vat': vatText,
  }, {context: 'Configurator price summary'});
  const ariaLabel = markedText
    .replace(COUNT_MARKER, countLabel)
    .replace(PRICE_MARKER, effectivePrice);
  const parts = markedText.split(MARKER_REGEX);

  return (
    <div
      className={cn('text-base my-xs', isFetching ? 'opacity-40' : '')}
      aria-label={ariaLabel}
    >
      {parts.map((part, i) => {
        if (part === COUNT_MARKER) return <span key={i} className="font-bold">{countLabel}</span>;
        if (part === PRICE_MARKER) return <span key={i} className="font-bold">{effectivePrice}</span>;
        return <React.Fragment key={i}>{part}</React.Fragment>;
      })}
    </div>
  );
};

export default PriceSummary;
